import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Step1 from "./step1";
import Step2 from "./step2";
import Step3 from "./step3";
import Step4 from "./step4";
import Step5 from "./step5";
import Step6 from "./step6";
import Cancel from "models/Cancel";
import { partnerRegistrationSteps } from "../../utils";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import {
  incrementVerifystep,
  resetState,
  setVerifystep,
} from "redux/features/PartnerRegistration";
import { toast, Toaster } from "sonner";
import { Img, Spinner } from "components";
// Same signatures as the Xano calls this screen was written against,
// answered by the new backend. See legacy-partner-registration-api.ts.
import {
  getPartnerIncompleteSteps,
  postOfficeAddressStep4Verification,
  postPartnerRegistrationStep1,
  postUpdateCommisionRatesStep5,
} from "service/moneeflo/legacy-partner-registration-api";
import { apiErrorMessage } from "service/moneeflo";
import {
  setEmail,
  setName,
  setPhone,
  setPartnertype,
  setStaffId,
  setPanType,
  setAddressLine1,
  setAddressLine2,
  setCity,
  setState,
  setPincode,
  setGst,
  setPropertyType,
  setResumeForm,
} from "../../redux/features/PartnerRegistration";
import "react-toastify/dist/ReactToastify.css";
import Stepper from "components/Stepper/newstepper";
import Navbar from "components/Navbar/Index";

// The stepper lists five labels; the sixth screen is the success page.
const DONE_STEP = 6;

/**
 * The six-step partner registration: details, PAN, Aadhaar, office
 * address, products and commission, done. Each step is answered by the new
 * backend; the record is created in `pending` at step 1 and becomes active
 * at step 5, as it did on Xano.
 *
 * Two ways in: "Register Partner" on the partners list starts a new
 * partner; "Register Partner" on a lead (or "Resume registration" on a
 * partner) arrives with `?lead=<id>`, and the wizard opens on the first
 * step that record still lacks, prefilled with what it already has.
 */
const PartnerRegistration: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setloading] = useState(false);
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const [confirmSubmit, setConfirmSubmit] = React.useState(false);
  const step2Ref = useRef<{ verifyPAN: () => void }>(null);
  const step3Ref = useRef<{ verifyAadhar: () => void }>(null);
  const [searchParams] = useSearchParams();

  const handleVerifyFromParent = () => step2Ref?.current?.verifyPAN();
  const handleVerifyAadhaarFromParent = () =>
    step3Ref?.current?.verifyAadhar();

  const {
    name,
    email,
    phone,
    Partnertype,
    Partnertypeother,
    staff_id,
    PanNumber,
    PanfrontBinary,
    adhaarFrontBinary,
    adhaarBackBinary,
    uid_number,
    currverifystep,
    address_line_1,
    address_line_2,
    state,
    pincode,
    addressBinary,
    gst,
    geolocation,
    photosBinary,
    citylabel,
    statelabel,
    PropertyType,
    resume_form,
    enabledProducts,
  } = useAppSelector((state) => state.PartnerRegistration);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location: any = useLocation();

  // The record being registered, if it already exists. The query string is
  // the current handover; the redux flag plus localStorage key is the one
  // the partner page's "Resume registration" has always used.
  // Snapshotted once: step 5 clears the resume flag when it finishes, and
  // deriving the id from the live flag made the mount effect below run
  // again and throw the desk back to step 1 instead of the success page.
  const [resumedId] = useState<string | null>(() =>
    resume_form ? localStorage.getItem("child_channel_id") : null,
  );
  const leadId = searchParams.get("lead") || resumedId || "";

  useEffect(() => {
    dispatch(resetState());
    setCurrentStep(1);
    dispatch(setVerifystep(0));
    if (leadId) {
      localStorage.setItem("child_channel_id", leadId);
      resumeRegistration(leadId);
    } else {
      localStorage.removeItem("child_channel_id");
    }
  }, [location.key, leadId]);

  // Step 5's own save buttons do not carry the product list; the adapter
  // reads it from here.
  useEffect(() => {
    localStorage.setItem("enabled_products", JSON.stringify(enabledProducts));
  }, [enabledProducts]);

  /** Opens on the first step the record lacks, prefilled with the rest. */
  const resumeRegistration = (channels_id: string): void => {
    setloading(true);
    getPartnerIncompleteSteps({ data: { channels_id } })
      .then((res) => {
        setloading(false);
        const r = res.data.result;
        dispatch(setName(r.name ?? ""));
        dispatch(setEmail(r.email ?? ""));
        dispatch(setPhone(r.phone ?? ""));
        dispatch(setPartnertype(r.type ?? ""));
        if (r.relationshil_manager) dispatch(setStaffId(r.relationshil_manager));
        dispatch(
          setPanType(
            r.pan_type === ""
              ? ""
              : r.pan_type === "Individual or Person"
                ? "individualPan"
                : "businessPan",
          ),
        );
        dispatch(setAddressLine1(r.address_line_1 ?? ""));
        dispatch(setAddressLine2(r.address_line_2 ?? ""));
        dispatch(setState(r.state ?? ""));
        dispatch(setCity(r.city ?? ""));
        dispatch(setPincode(r.pincode ?? ""));
        dispatch(setGst(r.gst_number ?? ""));
        dispatch(setPropertyType(r.property_type || "Owned"));
        localStorage.setItem("ApplicationId", r.id);
        localStorage.setItem(
          "ApplicationStatus",
          r.status === "active" ? "approved" : "pending",
        );
        dispatch(setVerifystep(res.data.step - 1));
        setCurrentStep(res.data.step);
        dispatch(setResumeForm(false));
      })
      .catch((err) => {
        setloading(false);
        toast.error(apiErrorMessage(err));
      });
  };

  const step1 = (): void => {
    setloading(true);
    // "Other" carries whatever was typed under it; the dropdown value alone
    // would record every freelancer and consultant as just "other".
    const category =
      Partnertype === "other" && Partnertypeother.trim()
        ? Partnertypeother.trim()
        : Partnertype;

    postPartnerRegistrationStep1({
      data: {
        lead_id: leadId || undefined,
        name,
        email,
        phone,
        type: category,
        staff_id,
      },
    })
      .then((res) => {
        if (res.data?.id) {
          localStorage.setItem("child_channel_id", res.data.id);
          localStorage.setItem("ApplicationId", res.data.channel.id);
          localStorage.setItem(
            "ApplicationStatus",
            res.data.channel.approval_status,
          );
        }
        if (res.data?.rm_assigned === false) {
          toast.warning(
            "Saved, but the relationship manager could not be set. Set it from the partner's page.",
          );
        }
        dispatch(incrementVerifystep());
        dispatch(setResumeForm(false));
        setCurrentStep((prev) => prev + 1);
        setloading(false);
      })
      .catch((err) => {
        setloading(false);
        toast.error(apiErrorMessage(err));
      });
  };

  const step4verification = (): void => {
    const channel_id = localStorage.getItem("child_channel_id");
    if (!channel_id) return;
    setloading(true);
    postOfficeAddressStep4Verification({
      data: {
        address_line_1,
        address_line_2,
        city: citylabel,
        state: statelabel,
        pincode,
        address_proof: addressBinary,
        property_type: PropertyType,
        gst_number: gst,
        channels_id: channel_id,
        geolocation,
        photo_1: photosBinary.photo_1,
        photo_2: photosBinary.photo_2,
        photo_3: photosBinary.photo_3,
        photo_4: photosBinary.photo_4,
      },
    })
      .then(() => {
        setloading(false);
        dispatch(incrementVerifystep());
        setCurrentStep((prev) => prev + 1);
      })
      .catch((e) => {
        setloading(false);
        toast.error(apiErrorMessage(e));
      });
  };

  /** Accept the defaults as they are. Step 5's own buttons handle edits. */
  const handleStep5Submit = (): void => {
    const channel_id = localStorage.getItem("child_channel_id");
    if (!channel_id) return;
    setloading(true);
    postUpdateCommisionRatesStep5({
      data: {
        child_channel: channel_id,
        accept_default: true,
        products: enabledProducts,
      },
    })
      .then((res) => {
        if (res.data.channel) {
          localStorage.setItem("ApplicationId", res.data.channel.id);
          localStorage.setItem(
            "ApplicationStatus",
            res.data.channel.approval_status,
          );
          dispatch(setResumeForm(false));
        }
        setCurrentStep((prev) => prev + 1);
        setloading(false);
      })
      .catch((e) => {
        setloading(false);
        toast.error(apiErrorMessage(e));
      });
  };

  /** "Please fill in Email, Partner type" rather than "all the fields". */
  const missingToast = (fields: [string, boolean][]) => {
    const missing = fields.filter(([, absent]) => absent).map(([label]) => label);
    toast.error(`Please fill in ${missing.join(", ")}`);
  };

  const handleNext = () => {
    if (currentStep == 1) {
      if (email === "" || Partnertype === "" || !staff_id) {
        missingToast([
          ["Email", email === ""],
          ["Partner type", Partnertype === ""],
          ["Relationship manager", !staff_id],
        ]);
        return;
      }
      if (currverifystep >= currentStep) {
        setCurrentStep(currentStep + 1);
      } else {
        step1();
      }
    } else if (currentStep === 2) {
      if (currverifystep >= currentStep) {
        setCurrentStep(currentStep + 1);
      } else if (PanNumber !== "" || PanfrontBinary !== "") {
        handleVerifyFromParent();
      } else {
        toast.error("Please upload the PAN card or enter the PAN number");
      }
    } else if (currentStep === 3) {
      if (currverifystep >= currentStep) {
        setCurrentStep(currentStep + 1);
      } else if (
        (adhaarFrontBinary !== "" && adhaarBackBinary !== "") ||
        uid_number !== ""
      ) {
        handleVerifyAadhaarFromParent();
      } else {
        toast.error(
          "Please upload both sides of the Aadhaar card or enter the Aadhaar number",
        );
      }
    } else if (currentStep === 4) {
      if (currverifystep >= currentStep) {
        setCurrentStep(currentStep + 1);
        return;
      }
      if (
        address_line_1 === "" ||
        state === "" ||
        citylabel === "" ||
        pincode === "" ||
        addressBinary === "" ||
        statelabel === "" ||
        PropertyType === ""
      ) {
        missingToast([
          ["Address line 1", address_line_1 === ""],
          ["State", state === "" || statelabel === ""],
          ["City", citylabel === ""],
          ["Pincode", pincode === ""],
          ["Type of property", PropertyType === ""],
          ["Address proof", addressBinary === ""],
        ]);
      } else if (String(pincode)?.length !== 6) {
        toast.error("Please enter valid pincode");
      } else {
        step4verification();
      }
    } else if (currentStep === 5) {
      if (currverifystep >= currentStep) {
        setCurrentStep((s) => s + 1);
      } else if (enabledProducts.length === 0) {
        toast.error("Please enable at least one product");
      } else {
        setConfirmSubmit(true);
      }
    }
  };

  const handlePrevious = () => setCurrentStep(currentStep - 1);

  const handleCancel = () => {
    dispatch(resetState());
    if (leadId) {
      navigate(`/leads/partner/${leadId}`);
    } else if (location?.state?.partnerID) {
      navigate(`/leads/partner/${location?.state?.partnerID}`);
    } else {
      navigate("/lead/contacts/partner");
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1
            setCurrentStep={setCurrentStep}
            disableButton={currverifystep >= currentStep}
          />
        );
      case 2:
        return (
          <Step2
            ref={step2Ref}
            setCurrentStep={setCurrentStep}
            disableButton={currverifystep >= currentStep}
          />
        );
      case 3:
        return (
          <Step3
            ref={step3Ref}
            setCurrentStep={setCurrentStep}
            disableButton={currverifystep >= currentStep}
          />
        );
      case 4:
        return (
          <Step4
            setCurrentStep={setCurrentStep}
            disableButton={currverifystep >= currentStep}
          />
        );
      case 5:
        return (
          <Step5
            setCurrentStep={setCurrentStep}
            disableButton={currverifystep >= currentStep}
          />
        );
      case 6:
        return <Step6 />;
      default:
        return null;
    }
  };

  return (
    <>
      {confirmCancel && (
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6" }}
          className="fixed top-0 left-0 z-[10] h-screen w-[100vw] flex justify-center items-center"
        >
          {" "}
          <Cancel
            title="Are You Sure You Want To Cancel This Partner Registration?"
            setConfirmCancel={setConfirmCancel}
            handleCancel={handleCancel}
          />{" "}
        </div>
      )}
      {confirmSubmit && (
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6" }}
          className="fixed top-0 left-0 z-[10] h-screen w-[100vw] flex justify-center items-center"
        >
          {" "}
          <Cancel
            title="Are You Sure You Want To Submit? Changes made in Default Commission will not be saved"
            setConfirmCancel={setConfirmSubmit}
            handleCancel={handleStep5Submit}
          />{" "}
        </div>
      )}
      {loading && <Spinner />}
      <div className="p-4 bg-gray-100 flex flex-col h-screen font-inter items-start justify-start mx-auto w-auto sm:w-full md:w-full">
        <div className="flex md:flex-col flex-col gap-4 md:gap-5 items-start justify-start w-full">
          <Navbar>
            <div className=" w-full flex justify-between">
              <div className=" flex gap-3 items-center">
                <Img
                  src="images/sidebar_img/remit.svg"
                  className="h-5"
                  alt=""
                />
                <div className="gap-2 flex flex-col">
                  <span className="text-sm font-semibold">
                    Partner Registration
                  </span>
                  <span className="text-xs font-normal text-[#999999]">
                    {leadId
                      ? "Register this lead as a partner"
                      : "Register new Partner with correct details"}
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="gap-2.5 flex items-center">
                  <Img src="images/overview/wrench.svg" alt="" />
                  <span className="text-sm font-semibold text-blue-700">
                    Help
                  </span>
                </div>
                {currentStep !== DONE_STEP && (
                  <AlertDialog>
                    <AlertDialogTrigger>
                      <button className="rounded-lg py-[11px] px-4 border bg-gray-button-gray-lite border-gray-border-gray-lite">
                        Cancel
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogPortal>
                      <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                      <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                        <AlertDialogHeader className="flex text-left gap-4 pt-8 pb-4 px-10 ">
                          <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#7E7E7E] ">
                            This action can not be undone. This will permanently
                            delete your progress and remove the actions
                            preformed.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                          <AlertDialogCancel
                            className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                            asChild
                          >
                            <button>Close</button>
                          </AlertDialogCancel>
                          <AlertDialogAction
                            className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                            asChild
                          >
                            <button className="" onClick={handleCancel}>
                              Confirm
                            </button>
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialogPortal>
                  </AlertDialog>
                )}
              </div>
            </div>
          </Navbar>
          <div className="gap-5 w-full flex h-full flex-1 flex-grow">
            <div className=" bg-white-A700 p-5 rounded-xl flex flex-col gap-5 w-full">
              <Stepper
                steps={partnerRegistrationSteps.map((val) => val.name)}
                currentStep={currentStep - 1}
              />
              {renderStep()}
              {currentStep !== DONE_STEP && (
                <div className="flex gap-3">
                  {currentStep !== 1 && (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      className="px-4 py-[11px] w-fit rounded-lg border border-blue-700 text-blue-700"
                    >
                      Previous
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-4 py-[11px] w-fit rounded-lg bg-blue-700 text-white-A700"
                  >
                    {currentStep === DONE_STEP - 1 ? "Submit" : "Next"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Toaster theme="dark" position="top-right" />
    </>
  );
};

export default PartnerRegistration;
